import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import {
  getStarterPath,
  saveStarterPath,
  JOURNEYS,
  type StarterStep,
  type JourneyId,
} from "@/lib/starter-path.functions";
import { getAllPosts } from "@/lib/posts.functions";

export const Route = createFileRoute("/admin/starter")({
  component: AdminStarter,
});


const SLOTS_PER_JOURNEY = 5;

function buildEmpty(): StarterStep[] {
  return JOURNEYS.flatMap((j) =>
    Array.from({ length: SLOTS_PER_JOURNEY }, (_, i) => ({
      journey: j.id,
      position: i + 1,
      title: "",
      url: "#",
    })),
  );
}

function AdminStarter() {
  const fetchSteps = useServerFn(getStarterPath);
  const save = useServerFn(saveStarterPath);
  const fetchPosts = useServerFn(getAllPosts);

  const { data, refetch } = useQuery({
    queryKey: ["admin-starter-path"],
    queryFn: () => fetchSteps(),
  });
  const { data: posts } = useQuery({
    queryKey: ["admin-starter-posts"],
    queryFn: () => fetchPosts({ data: { lang: "tr" } }),
  });

  const postOptions = useMemo(
    () =>
      (posts ?? []).map((p: any) => ({
        slug: p.slug as string,
        title: p.title as string,
        url: `/yazilar/${p.slug}`,
      })),
    [posts],
  );


  const [steps, setSteps] = useState<StarterStep[]>(() => buildEmpty());
  const [savedAt, setSavedAt] = useState<string | null>(null);

  useEffect(() => {
    if (!data) return;
    const next = buildEmpty().map((slot) => {
      const found = data.find(
        (r) => r.journey === slot.journey && r.position === slot.position,
      );
      return found ?? slot;
    });
    setSteps(next);
  }, [data]);

  const saveMut = useMutation({
    mutationFn: () => save({ data: { steps } }),
    onSuccess: async () => {
      setSavedAt(new Date().toLocaleTimeString("tr-TR"));
      await refetch();
    },
    onError: (e: Error) => alert("Kaydetme başarısız: " + e.message),
  });

  const update = (
    journey: JourneyId,
    position: number,
    patch: Partial<StarterStep>,
  ) => {
    setSteps((prev) =>
      prev.map((s) =>
        s.journey === journey && s.position === position
          ? { ...s, ...patch }
          : s,
      ),
    );
  };

  return (
    <div className="space-y-10">
      <div>
        <h1 className="font-serif text-3xl">Giriş Yolu — Dört Temalı Yol</h1>
        <p className="text-sm text-ink/85 mt-2 max-w-2xl">
          Her temaya 5 yazı ekleyebilirsiniz. URL alanına dahili bir yol
          (örn. <code>/yazilar/slug</code>) veya tam bir bağlantı
          yapıştırabilirsiniz. Boş bıraktığınız satırlar ziyaretçilere
          gösterilmez.
        </p>
      </div>

      {JOURNEYS.map((j) => {
        const journeySteps = steps.filter((s) => s.journey === j.id);
        return (
          <section
            key={j.id}
            className="border border-ink/15 rounded-lg p-5 space-y-4 bg-paper/60"
          >
            <header>
              <span className="text-[10px] uppercase tracking-[0.25em] text-clay font-medium">
                {j.id}
              </span>
              <h2 className="font-serif text-2xl mt-1">{j.label}</h2>
              <p className="text-sm text-ink/60 mt-1">{j.intro}</p>
            </header>

            <div className="space-y-3">
              {journeySteps.map((s) => {
                const matched = postOptions.find((p) => p.url === s.url);
                return (
                <div
                  key={`${j.id}-${s.position}`}
                  className="border border-ink/10 rounded p-3 space-y-2"
                >
                  <div className="flex items-center gap-3">
                    <span className="font-serif text-xl text-clay tabular-nums w-8">
                      {String(s.position).padStart(2, "0")}
                    </span>
                    <select
                      value={matched?.slug ?? ""}
                      onChange={(e) => {
                        const slug = e.target.value;
                        if (!slug) {
                          update(j.id, s.position, { title: "", url: "#" });
                          return;
                        }
                        const p = postOptions.find((o) => o.slug === slug);
                        if (p) update(j.id, s.position, { title: p.title, url: p.url });
                      }}
                      className="flex-1 border border-ink/20 rounded px-3 py-2 text-sm bg-paper"
                    >
                      <option value="">— Yazı seç —</option>
                      {postOptions.map((p) => (
                        <option key={p.slug} value={p.slug}>
                          {p.title}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-3 pl-11">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-ink/85 shrink-0">
                      Başlık
                    </span>
                    <input
                      type="text"
                      value={s.title}
                      onChange={(e) =>
                        update(j.id, s.position, { title: e.target.value })
                      }
                      placeholder="Yazı başlığı (otomatik dolar)"
                      className="flex-1 border border-ink/20 rounded px-3 py-2 text-sm bg-paper"
                    />
                  </div>
                  <div className="flex items-center gap-3 pl-11">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-ink/85 shrink-0">
                      URL
                    </span>
                    <input
                      type="text"
                      value={s.url}
                      onChange={(e) =>
                        update(j.id, s.position, { url: e.target.value })
                      }
                      placeholder="/yazilar/slug veya https://..."
                      className="flex-1 border border-ink/20 rounded px-3 py-2 text-sm bg-paper font-mono"
                    />
                  </div>
                </div>
                );
              })}

            </div>
          </section>
        );
      })}

      <div className="flex items-center gap-4 pt-4 border-t border-border sticky bottom-4 bg-paper/95 backdrop-blur py-3">
        <button
          onClick={() => saveMut.mutate()}
          disabled={saveMut.isPending}
          className="px-5 py-2 bg-ink text-paper rounded-full text-xs uppercase tracking-[0.2em] disabled:opacity-50"
        >
          {saveMut.isPending ? "Kaydediliyor..." : "Hepsini Kaydet"}
        </button>
        {savedAt && (
          <span className="text-xs text-moss">Kaydedildi · {savedAt}</span>
        )}
      </div>
    </div>
  );
}
